import { Injectable } from '@angular/core';
import { NetwordDetectionService } from 'modules/app/services/network-detection.service';
import { environment } from 'environments/environment';
import DesktopConfig from 'modules/desktop/services/config';
import { ApiGatewayHelperService } from 'modules/app/services/apigateway-helper.service';
import { nanoid, customAlphabet } from 'nanoid';
import { appSetting } from "../../../config/appSettings";
import { ExternalData } from "libs-frontend-external-data/dist/js/external-data.js"

const custNanoId = customAlphabet('1234567890abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ', 10);

@Injectable()
export class DesktopAppEventsService {

    CONSTANTS = {
        EVENT_ACTIONS: {
            CREATE: 'CREATE',   
            UPDATE: 'UPDATE',
            DELETE: 'DELETE',
            START: 'START',         // when an action [EVENT_CATEGORIES] is started
            COMPLETE: 'COMPLETE',   // when an action [EVENT_CATEGORIES] is completed
            DOWNLOAD: 'DOWNLOAD'
        },
        EVENT_CATEGORIES: {
            SESSION: 'CUSTOM_DESTKOP_SESSION',              // login, logout, app close
            DOWNLOAD: 'CUSTOM_DESTKOP_CONTENT_DOWNLOAD',    // ebook download and PE download
            CONTENT: 'CUSTOM_DESTKOP_CONTENT',              // ebook open and close
        },
        EVENT_DATA_CONNECTION: {
            ONLINE: 'online',
            OFFLINE: 'offline'
        },
        EVENT_DATA_CONTENT_TYPE: {
            BOOK: 'book',
            PE: 'practice-extra'
        },
        EVENT_TYPES: {
            LEARNING: 'learning',       // content related events generated on user actions [book launch]
            SYSTEM: 'system'            // download, login
        },
        OS: {
            WINDOWS: 'windows',
            MAC: 'mac',
            LINUX: 'linux'
        },
        VERBS: {
            NAVIGATED: 'navigated'
        },
        NAVIGATED_PAGE_NAMES: {     // navigated-to and navgated-from
            LOGIN: 'login',
            LAUNCH: 'launch',
            DASHBOARD: 'dashboard',
            EREADER: 'eReader',
            PRODUCTDETAILS: 'product-details',
            OFFLINE_NO_CONNECTION: 'offline-no-connection',
            CLOSE: 'close',
            CLASS: 'class',
            LIBRARY: 'library',
            INDEPENDENT_LEARNING: 'independent-learning'
        },
        NUMBER_TO_MONTH: {
            0: 'Jan',
            1: 'Feb',
            2: 'Mar',
            3: 'Apr',
            4: 'May',
            5: 'Jun',
            6: 'Jul',
            7: 'Aug',
            8: 'Sept',
            9: 'Oct',
            10: 'Nov',
            11: 'Dec',
        }
    }

    constantValues = {
        isOnline: true,
        currentOS: this.CONSTANTS.OS.WINDOWS,
    }

    userData = {
        extUserId: undefined,
        userRole: undefined,
        country: undefined
    }

    contentLaunchEventFired = false;
    contentLaunchEventData;
    lastVisitedPage;
    currentPage;
    navigationFromContext;
    externalDataInstance;
    isUserLoggedIn = false;

    // a unique session id generate everytime a user logs in by entering username and password
    loginSessionId;

    // a unique id generated in three conditions
    //  - If someone enters credentials online and logs in
    //  - If someone launches online and are automatically logged in in online mode
    //  - If someone launches offline and are automatically logged in in offline mode
    launchSessionId;

    constructor(
        private networkService: NetwordDetectionService,
        private apiGatewayHelperService: ApiGatewayHelperService,
    ) {
        if (environment.isDesktopApp) {
            this.setDefaultVariables();
            this.networkService.monitorStatus().subscribe(async (isOnline) => {
                if (this.constantValues.isOnline != isOnline) {
                    this.constantValues.isOnline = isOnline;
                }
            });

            // this.displayDesktopAppEvents();

            let routePrefix = this.apiGatewayHelperService.getApiGatewayPrefixUrl();
            routePrefix = DesktopConfig.app.api.baseUrl + routePrefix;
            let serverRoutePrefix = routePrefix.substr(0, routePrefix.length - 1);
            let options = {
                retryCount: 3,
                batchSize: 2048,
                retryCallback: this.retryCallback,
                serverRoutePrefix,
                getQueryParamsObjFunction: this.getxAPIQueryParams.bind(this)
            };

            this.externalDataInstance = new ExternalData(options);
        }

    }

    getxAPIQueryParams() {
        if (environment.isDesktopApp) {
            return this.apiGatewayHelperService.getAPIAuthToken();
        } else {
            return Promise.resolve({});
        }
    }

    async setDefaultVariables() {
        let os = await DLS_IPCCallers.GET_CURRENT_OS();

        if (os == 'win32') {
            this.constantValues.currentOS = this.CONSTANTS.OS.WINDOWS;
        } else if (os == 'darwin') {
            this.constantValues.currentOS = this.CONSTANTS.OS.MAC;
        } else {
            this.constantValues.currentOS = this.CONSTANTS.OS.LINUX;
        }

        this.constantValues.isOnline = await this.networkService.isOnline();
    }

    setUserData(uid, userRole, country) {
        this.userData.extUserId = uid;
        this.userData.userRole = userRole;
        this.userData.country = country;
    }

    resetUserData() {
        this.userData = {
            extUserId: undefined,
            userRole: undefined,
            country: undefined
        };
        this.lastVisitedPage = undefined;
        this.currentPage = undefined;
        this.navigationFromContext = undefined;
    }

    setLastVisitedPage(lastVisitedPage) {
        this.lastVisitedPage = lastVisitedPage;
    }

    getLastVisitedPage() {
        return this.lastVisitedPage;
    }

    setCurrentPage(currentPage) {
        this.currentPage = currentPage;
    }

    getCurrentPage() {
        return this.currentPage;
    }

    setNavigationFromContext(context) {
        this.navigationFromContext = context;
    }

    getNavigationFromContext() {
        return this.navigationFromContext;
    }


    async generateDesktopEvent(data) {
        let extUserId = data.ext_actor_id || this.userData.extUserId;
        if (extUserId) {

            // if session close event is recieved and content close event is not recieved then 
            // user has closed the app while book is launched, for this, create and store a content close event.
            if (data.category == this.CONSTANTS.EVENT_CATEGORIES.CONTENT && data.action == this.CONSTANTS.EVENT_ACTIONS.START) {
                this.contentLaunchEventFired = true;
                this.contentLaunchEventData = data;
            } else if (data.category == this.CONSTANTS.EVENT_CATEGORIES.CONTENT && data.action == this.CONSTANTS.EVENT_ACTIONS.COMPLETE) {
                this.contentLaunchEventFired = false;
                this.contentLaunchEventData = undefined;
            } else if (data.category == this.CONSTANTS.EVENT_CATEGORIES.SESSION) {
                if (this.contentLaunchEventFired) {
                    await this.generateContentCloseEvent(this.contentLaunchEventData)
                }
            }
            await this.addDesktopEventToDB(data);


            // sync desktop events in online mode for
            // login, logout, app close.
            // download start / end.
            // ereader end.
            if (this.constantValues.isOnline) {
                if (data.category == this.CONSTANTS.EVENT_CATEGORIES.DOWNLOAD || (data.category == this.CONSTANTS.EVENT_CATEGORIES.SESSION && data.action == this.CONSTANTS.EVENT_ACTIONS.START)) {
                    await this.syncDesktopEvents(extUserId);
                }
            }

        }
    }

    generateDownloadEvent(userId, userRole, productCode, productVersion, productType, smartDownload, autoUpdate, action, category) {

        userId = userId || this.userData.extUserId;
        userRole = userRole || this.userData.userRole;
        let os = this.constantValues.currentOS;
        let adjustedTimestamp = this._getAdjustedTimestamp();

        return {
            "data": {
                "actorid": userId,
                "product_code": productCode,
                "product_version": productVersion,
                "product_type": productType,
                "xapi": false,
                "smart_download": smartDownload,
                "user_role": userRole,
                "user_country": this.userData.country,
                "auto_update": autoUpdate,
                "os": os,
                "timestamp": adjustedTimestamp,
                "session_id": this.getSessionId(),
                "app_version": DesktopConfig.app.version
            },
            "action": action,
            "category": category,
            "type": this.CONSTANTS.EVENT_TYPES.SYSTEM,
            "originatorid": userId
        }

    }

    generateSessionEvent(userId, userRole, navigatedFrom, navigatedTo, action, category) {


        if(action == this.CONSTANTS.EVENT_ACTIONS.START && category == this.CONSTANTS.EVENT_CATEGORIES.SESSION) {
            this.isUserLoggedIn = true;
        }

        if(action == this.CONSTANTS.EVENT_ACTIONS.COMPLETE && category == this.CONSTANTS.EVENT_CATEGORIES.SESSION) {
            this.isUserLoggedIn = false;
        }


        userId = userId || this.userData.extUserId;
        userRole = userRole || this.userData.userRole;
        let os = this.constantValues.currentOS;
        let adjustedTimestamp = this._getAdjustedTimestamp();
        let isOnline = this.CONSTANTS.EVENT_DATA_CONNECTION.ONLINE;
        if (!this.constantValues.isOnline) {
            isOnline = this.CONSTANTS.EVENT_DATA_CONNECTION.OFFLINE;
        }

        return {
            "data": {
                "actorid": userId,
                "mode": isOnline,
                "os": os,
                "timestamp": adjustedTimestamp,
                "verb": this.CONSTANTS.VERBS.NAVIGATED,
                "xapi": false,
                "user_role": userRole,
                "user_country": this.userData.country,
                "navigated": {
                    "from": {
                        "page": navigatedFrom
                    },
                    "to": {
                        "page": navigatedTo
                    }
                },
                "session_id": this.getSessionId(),
                "app_version": DesktopConfig.app.version
            },
            "action": action,
            "category": category,
            "type": this.CONSTANTS.EVENT_TYPES.LEARNING,
            "originatorid": userId
        }

    }

    generateContentEvent(userId, userRole, productCode, wasDownloaded, navigatedFrom, navigatedFromContext, navigatedTo, action, category) {

        userId = userId || this.userData.extUserId;
        userRole = userRole || this.userData.userRole;
        let os = this.constantValues.currentOS;
        let adjustedTimestamp = this._getAdjustedTimestamp();
        let isOnline = this.CONSTANTS.EVENT_DATA_CONNECTION.ONLINE;
        if (!this.constantValues.isOnline) {
            isOnline = this.CONSTANTS.EVENT_DATA_CONNECTION.OFFLINE;
        }

        return {
            "data": {
                "actorid": userId,
                "mode": isOnline,
                "product_code": productCode,
                "downloaded": wasDownloaded,
                "os": os,
                "timestamp": adjustedTimestamp,
                "verb": this.CONSTANTS.VERBS.NAVIGATED,
                "xapi": false,
                "user_role": userRole,
                "user_country": this.userData.country,
                "navigated": {
                    "from": {
                        "page": navigatedFrom,
                        "context": navigatedFromContext
                    },
                    "to": {
                        "page": navigatedTo
                    }
                },
                "session_id": this.getSessionId(),
                "app_version": DesktopConfig.app.version
            },
            "action": action,
            "category": category,
            "type": this.CONSTANTS.EVENT_TYPES.LEARNING,
            "originatorid": userId
        }

    }

    async generateContentCloseEvent(data) {

        data.action = this.CONSTANTS.EVENT_ACTIONS.COMPLETE;
        data.category = this.CONSTANTS.EVENT_CATEGORIES.CONTENT;
        data.data.navigated = {
            "from": {
                "page": this.CONSTANTS.NAVIGATED_PAGE_NAMES.EREADER,
            },
            "to": {
                "page": this.CONSTANTS.NAVIGATED_PAGE_NAMES.CLOSE
            }
        }
        data.data.timestamp = this._getAdjustedTimestamp();

        await this.generateDesktopEvent(data);
    }

    async addDesktopEventToDB(modifiedData) {

        modifiedData.extraMetadata = {
            uniqueId: `${modifiedData.originatorid}~~${this._getPrefixedUniqueId(16, "")}`,
            schemaVersion: 2
        }
        await DLS_IPCCallers.ADD_DESKTOP_APP_EVENT(modifiedData);
    }

    async getDesktopAppEvents() {
        return await DLS_IPCCallers.GET_ALL_DESKTOP_APP_EVENTS();
    }

    async displayDesktopAppEvents() {

        let desktopEventsArray = await this.getDesktopAppEvents();
        let combinedArray = [];
        desktopEventsArray.forEach(user => {
            user.userEvents.forEach(events => {
                let flattenedObject = {};
                flattenedObject['uid'] = user.uid;
                Object.keys(events).forEach(key => {
                    if (key != 'data') {
                        flattenedObject[key] = events[key];
                    }
                });
                Object.keys(events.data).forEach(key => {
                    flattenedObject[key] = events.data[key];
                });
                combinedArray.push(flattenedObject)
            });
        });
        console.log(combinedArray);

    }

    
    async syncDesktopEvents(extUserId) {

        try {

            if (!this.constantValues.isOnline) return;

            let desktopEventsArray = await this.getDesktopAppEvents();
            let sanitizedData = this._sanitizeDesktopAppEventsData(desktopEventsArray, extUserId);

            let data = await this.externalDataInstance.postData(sanitizedData);
            // console.log("sanitizedData: ", sanitizedData);
            // console.log("posted data: ", data);
            await this._updateDBWithEventsResponse(data, extUserId);

        } catch (err) {
            console.log("Error while syncing events", err);
        }

    }


    _sanitizeDesktopAppEventsData(desktopEventsArray, extUserId) {

        let userData = desktopEventsArray.find(userEventData => userEventData.uid == extUserId);

        let sanitizedData = {
            feature: 'desktop-events',
            data: []
        }

        userData.userEvents.forEach(event => {

            let uniqueId = event.extraMetadata.uniqueId;

            // getting date in format YYYY-MM-DD
            let evDate = ((new Date()).toISOString().split("T"))[0];

            event.data.uuid = uniqueId;
            delete event.extraMetadata;

            // opts.folderStructure = "category=<CATEGORY>/action=<action>/date=<YYYY-MM-DD>/<uuid>.json"
            // E.G.: "category=CUSTOM_DESTKOP_SESSION/action=start/date=2022-03-09/<uuid>.json"
            sanitizedData.data.push({
                id: uniqueId,
                content: event,
                opts: {
                    'folder-structure': `category=${event.category}/action=${(event.action || "").toLowerCase()}/date=${evDate}`
                }
            });

        });

        return sanitizedData;

    }


    
    async _updateDBWithEventsResponse(data, extUserId) {
        await DLS_IPCCallers.UPDATE_DB_WITH_SERVER_RESPONSE({ uid: extUserId, response: data });
    }

    _getAdjustedTimestamp() {
        let serverTimeOffsetLSKey = `${appSetting.localStorage.foc}-serverTimeSyncOffset`;
        let offset = +localStorage.getItem(serverTimeOffsetLSKey);
        if ((offset != null) && !isNaN(offset)) {
            offset = +offset;
        } else {
            offset = 0;
        }
        let adjustedTime = Date.now() - offset;
        return adjustedTime;
    }

    _getPrefixedUniqueId(chars, prefix) {
        let uniqueId = nanoid(chars);
        return prefix + uniqueId;
    }

    retryCallback(retryCount, error) {
        console.log(retryCount);
        console.log(error);
    }

    /**
     * method to update login session id
     * use the sessionId if passed
     * generate new id if param is not passed
     * 
     * new session id is generated when a user logs in by entering username and password
     * for pre-existing sessions the id will be fetched from db and passed to this function
     * 
     * @param sessionId (optional)
     * 
     * returns updated session id
     */
    updateLoginSessionId(sessionId?){
        this.loginSessionId = sessionId || custNanoId();
        return this.loginSessionId;
    }

    /**
     * launch session id needs to be freshly generated 
     *  - If someone enters credentials online and logs in
     *  - If someone launches online and are automatically logged in in online mode
     *  - If someone launches offline and are automatically logged in in offline mode
     */
    updateLaunchSessionId(){
        this.launchSessionId = custNanoId();
        return this.launchSessionId;
    }

    /**
     * session id is a combination of loginSessionId and launchSessionId
     */
    getSessionId(){
        if(!this.loginSessionId){
            throw ("Error: Login SessionId absent!");
        }

        if(!this.launchSessionId){
            throw ("Error: Launch SessionId absent!");
        }

        return `${this.loginSessionId}-${this.launchSessionId}`;
    }

    /**
     * session ids need to be unset on user logout
     */
    resetSessionInfo(){
        this.loginSessionId = null;
        this.launchSessionId = null;
    }

    /**
     * Check if user is currently logged in based on the events data. 
     * @returns true | false based on users login status.
     */
    checkIfUserIsLoggedIn() {
        return this.isUserLoggedIn;
    }

}
